import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { Caption1 } from "@fluentui/react-components";
import { todayISO } from "@/engine/date";
import { RecordStatusBadge } from "@/features/records/record-ui";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatDate } from "@/lib/date-format";
import { planHref } from "@/shell/project-nav";
import { useCatalogStore } from "@/store/catalog-store";
import {
  RAID_AGE_LABEL,
  RAID_BUCKET_LABEL,
  RAID_BUCKETS,
  RAID_SCORE_LABEL,
  collectRaid,
  filterRaid,
  raidScoreLabel,
  sortRaid,
  type RaidAge,
  type RaidBucket,
  type RaidRow,
  type RaidScoreBand,
  type RaidSource,
} from "@/features/pm/portfolio-raid";

const useStyles = makeStyles({
  clip: { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  late: { color: tokens.colorPaletteRedForeground1 },
  quiet: { color: tokens.colorNeutralForeground3 },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  empty: { padding: "24px 0" },
});

export interface RaidUiState {
  bucket: RaidBucket | "all";
  setBucket: (value: RaidBucket | "all") => void;
  /** `current`는 지금 워크스페이스, `all`은 워크스페이스를 가로지른다. */
  workspaceId: string;
  setWorkspaceId: (value: string) => void;
  projectId: string;
  setProjectId: (value: string) => void;
  owner: string;
  setOwner: (value: string) => void;
  score: RaidScoreBand;
  setScore: (value: RaidScoreBand) => void;
  age: RaidAge;
  setAge: (value: RaidAge) => void;
  overdueOnly: boolean;
  setOverdueOnly: (value: boolean) => void;
  plans: Array<{ id: string; name: string }>;
  owners: string[];
  workspaces: Array<{ value: string; label: string }>;
  filtered: RaidRow[];
  openCount: number;
  reset: () => void;
}

export function usePortfolioRaid(query: string): RaidUiState {
  const workspaces = useCatalogStore((state) => state.workspaces);
  const activeWorkspaceId = useCatalogStore((state) => state.activeWorkspaceId);
  const projects = useCatalogStore((state) => state.projects);
  const [bucket, setBucket] = useState<RaidBucket | "all">("all");
  const [workspaceId, setWorkspaceId] = useState("current");
  const [projectId, setProjectId] = useState("all");
  const [owner, setOwner] = useState("all");
  const [score, setScore] = useState<RaidScoreBand>("all");
  const [age, setAge] = useState<RaidAge>("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const openRows = useMemo(() => {
    const sources: RaidSource[] = [];
    for (const workspace of Object.values(workspaces)) {
      const list = workspace.id === activeWorkspaceId ? projects : workspace.projects;
      for (const project of Object.values(list)) {
        if (project.lifecycle === "trashed") continue;
        sources.push({
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          projectId: project.id,
          projectName: project.name,
          records: project.records ?? [],
        });
      }
    }
    return sortRaid(collectRaid(sources, todayISO()));
  }, [workspaces, activeWorkspaceId, projects]);

  const resolvedWorkspace = workspaceId === "current" ? activeWorkspaceId : workspaceId;
  const scoped = useMemo(
    () => (resolvedWorkspace === "all" ? openRows : openRows.filter((row) => row.workspaceId === resolvedWorkspace)),
    [openRows, resolvedWorkspace],
  );

  const filtered = useMemo(
    () => filterRaid(scoped, { query, bucket, projectId, owner, score, age, overdueOnly }),
    [scoped, query, bucket, projectId, owner, score, age, overdueOnly],
  );

  const plans = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of scoped) map.set(row.projectId, row.projectName);
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "ko"));
  }, [scoped]);

  const workspaceOptions = useMemo(() => {
    const map = new Map<string, string>();
    if (activeWorkspaceId) map.set(activeWorkspaceId, workspaces[activeWorkspaceId]?.name ?? "현재 워크스페이스");
    for (const row of openRows) map.set(row.workspaceId, row.workspaceName);
    const options = [...map.entries()].map(([id, name]) => ({ value: id === activeWorkspaceId ? "current" : id, label: name }));
    const current = options.find((item) => item.value === "current");
    const rest = options.filter((item) => item.value !== "current").sort((a, b) => a.label.localeCompare(b.label, "ko"));
    return [...(current ? [current] : []), ...rest, { value: "all", label: "전체" }];
  }, [openRows, workspaces, activeWorkspaceId]);

  const owners = useMemo(() => {
    const names = new Set<string>();
    for (const row of scoped) for (const name of row.owners) names.add(name);
    return [...names].sort((a, b) => a.localeCompare(b, "ko"));
  }, [scoped]);

  const chooseWorkspace = (value: string) => {
    setWorkspaceId(value);
    setProjectId("all");
  };

  const reset = () => {
    setBucket("all");
    setWorkspaceId("current");
    setProjectId("all");
    setOwner("all");
    setScore("all");
    setAge("all");
    setOverdueOnly(false);
  };

  return {
    bucket, setBucket, workspaceId, setWorkspaceId: chooseWorkspace, projectId, setProjectId, owner, setOwner, score, setScore, age, setAge, overdueOnly, setOverdueOnly,
    plans, owners, filtered, openCount: scoped.length, reset, workspaces: workspaceOptions,
  };
}

export function PortfolioRaidFilters({ raid }: { raid: RaidUiState }) {
  return (
    <>
      <SegmentedControl
        ariaLabel="종류"
        value={raid.bucket}
        onChange={raid.setBucket}
        options={[{ value: "all", label: "전체" }, ...RAID_BUCKETS.map((bucket) => ({ value: bucket, label: RAID_BUCKET_LABEL[bucket] }))]}
      />
      <FuiFilterSelect
        label="워크스페이스"
        ariaLabel="워크스페이스"
        value={raid.workspaces.some((item) => item.value === raid.workspaceId) ? raid.workspaceId : "all"}
        options={raid.workspaces}
        onValueChange={raid.setWorkspaceId}
      />
      <FuiFilterSelect
        label="플랜"
        ariaLabel="플랜"
        value={raid.projectId}
        options={[{ value: "all", label: "전체" }, ...raid.plans.map((plan) => ({ value: plan.id, label: plan.name }))]}
        onValueChange={raid.setProjectId}
      />
      <FuiFilterSelect
        label="담당"
        ariaLabel="담당"
        value={raid.owner}
        options={[{ value: "all", label: "전체" }, ...raid.owners.map((name) => ({ value: name, label: name }))]}
        onValueChange={raid.setOwner}
      />
      <FuiFilterSelect
        label="점수"
        ariaLabel="점수"
        value={raid.score}
        options={(Object.keys(RAID_SCORE_LABEL) as RaidScoreBand[]).map((key) => ({ value: key, label: RAID_SCORE_LABEL[key] }))}
        onValueChange={(value) => raid.setScore(value as RaidScoreBand)}
      />
      <FuiFilterSelect
        label="연령"
        ariaLabel="연령"
        value={raid.age}
        options={(Object.keys(RAID_AGE_LABEL) as RaidAge[]).map((key) => ({ value: key, label: RAID_AGE_LABEL[key] }))}
        onValueChange={(value) => raid.setAge(value as RaidAge)}
      />
      <FuiFilterSelect
        label="기한"
        ariaLabel="기한"
        value={raid.overdueOnly ? "overdue" : "all"}
        options={[
          { value: "all", label: "전체" },
          { value: "overdue", label: "기한 지남" },
        ]}
        onValueChange={(value) => raid.setOverdueOnly(value === "overdue")}
      />
    </>
  );
}

export function PortfolioRaidTable({ raid }: { raid: RaidUiState }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const navigate = useNavigate();

  const open = (row: RaidRow) => {
    const catalog = useCatalogStore.getState();
    if (row.workspaceId !== catalog.activeWorkspaceId) catalog.switchWorkspace(row.workspaceId);
    navigate(`${planHref(row.projectId, "records")}?open=${row.recordId}`);
  };

  if (raid.openCount === 0) {
    return (
      <div className={styles.empty}>
        <GhostEmptyState title="열린 기록이 없습니다" description="리스크, 이슈, 결정·요청, 가정이 열리면 여기에 모입니다." />
      </div>
    );
  }
  if (raid.filtered.length === 0) {
    return <Caption1 className={styles.quiet}>조건에 맞는 기록이 없습니다.</Caption1>;
  }

  return (
    <div className={table.frame}>
      <table className={table.table} aria-label="포트폴리오 RAID">
        <thead>
          <tr>
            <th className={table.th}>종류</th>
            <th className={table.th}>번호</th>
            <th className={table.th}>제목</th>
            <th className={table.th}>플랜</th>
            <th className={table.th}>워크스페이스</th>
            <th className={table.th}>담당</th>
            <th className={mergeClasses(table.th, table.num, styles.num)} aria-sort="descending">점수</th>
            <th className={mergeClasses(table.th, table.num, styles.num)}>연령</th>
            <th className={table.th}>기한</th>
            <th className={table.th}>상태</th>
          </tr>
        </thead>
        <tbody>
          {raid.filtered.map((row) => (
            <tr
              key={row.key}
              className={styles.row}
              tabIndex={0}
              onClick={() => open(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  open(row);
                }
              }}
            >
              <td className={table.td}>{RAID_BUCKET_LABEL[row.bucket]}</td>
              <td className={table.td}>{row.code}</td>
              <td className={table.td}><span className={styles.clip} title={row.title}>{row.title}</span></td>
              <td className={table.td}><span className={styles.clip} title={row.projectName}>{row.projectName}</span></td>
              <td className={table.td}><span className={styles.clip} title={row.workspaceName}>{row.workspaceName}</span></td>
              <td className={table.td}><span className={styles.clip} title={row.ownerLabel}>{row.ownerLabel}</span></td>
              <td className={mergeClasses(table.td, table.num, styles.num)}>{raidScoreLabel(row)}</td>
              <td className={mergeClasses(table.td, table.num, styles.num)}>{row.ageDays}일</td>
              <td className={table.td}>
                {row.due ? (
                  <span className={row.overdue ? styles.late : undefined}>{formatDate(row.due, "numeric")}{row.overdue ? " 지남" : ""}</span>
                ) : "—"}
              </td>
              <td className={table.td}><RecordStatusBadge kind={row.kind} status={row.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
